import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Flame, MessageCircle, CheckCheck, Play } from "lucide-react";
import { EASE } from "./Reveal";
import { WaitlistForm } from "./WaitlistForm";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const PhoneCard = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60, rotate: 4 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ duration: 1.4, delay: 0.7, ease: EASE }}
      style={{ perspective: 1000 }}
      className="relative mx-auto w-full max-w-sm"
    >
      <div className="absolute -inset-10 rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="absolute -right-8 top-10 h-40 w-40 rounded-full bg-orange-500/10 blur-3xl" />
      <motion.div
        ref={ref}
        data-testid="hero-phone-mockup"
        onMouseMove={onMove}
        onMouseLeave={() => {
          mx.set(0.5);
          my.set(0.5);
        }}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative rounded-[2rem] border border-white/10 bg-[#131316]/90 p-5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl"
      >
        <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/20">
              <MessageCircle className="h-4 w-4 text-emerald-400" />
            </span>
            <div>
              <p className="text-xs font-semibold">YateAviso Bot</p>
              <p className="mono text-[10px] text-emerald-400">en línea</p>
            </div>
          </div>
          <span className="mono rounded-full border border-orange-500/30 bg-orange-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-orange-400">
            Vence hoy
          </span>
        </div>

        <div className="space-y-3" style={{ transform: "translateZ(30px)" }}>
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.4, duration: 0.7, ease: EASE }}
            className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/5 p-3 text-xs leading-relaxed text-slate-300"
          >
            Hola <span className="text-emerald-400">Doña Carmen</span>, te recuerdo que tu factura
            #1042 por <span className="font-semibold text-slate-100">S/ 480.00</span> vence hoy.
            Puedes pagar aquí: yape.go/yateaviso
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 2.1, duration: 0.7, ease: EASE }}
            className="ml-auto max-w-[70%] rounded-2xl rounded-tr-sm bg-emerald-600/20 p-3 text-xs text-emerald-100"
          >
            ¡Gracias por avisar! Ya realicé el pago
            <span className="mt-1 flex items-center justify-end gap-1 text-[10px] text-emerald-400">
              10:42 <CheckCheck className="h-3 w-3" />
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 2.7, duration: 0.6, ease: EASE }}
            className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2"
          >
            <span className="mono text-[10px] uppercase tracking-widest text-emerald-300">Cobro confirmado</span>
            <span className="font-display text-sm font-bold text-emerald-400">S/ 480.00</span>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const KineticHero = () => (
  <section id="inicio" data-testid="hero-section" className="relative overflow-hidden px-4 pt-36 pb-20 sm:px-6 lg:pt-44 lg:pb-28">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "72px 72px",
      }}
    />
    <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />

    <div className="mx-auto grid max-w-6xl items-center gap-16 lg:grid-cols-2">
      <div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5"
        >
          <Flame className="h-3.5 w-3.5 text-orange-400" />
          <span className="mono text-[11px] uppercase tracking-[0.15em] text-orange-300">
            Acceso anticipado · 50% dcto de por vida
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl font-black leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.4}>Nunca más sientas</MaskedLine>
          <MaskedLine delay={0.55}>pena de cobrar</MaskedLine>
          <MaskedLine delay={0.7}>
            <span className="text-emerald-400">tu dinero.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: EASE }}
          className="mt-6 max-w-lg text-base leading-relaxed text-slate-400 sm:text-lg"
        >
          YateAviso envía recordatorios educados y automáticos por WhatsApp a tus clientes antes y
          después del vencimiento. Automatiza tu flujo de caja sin perder la buena relación.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: EASE }}
          className="mt-8 max-w-lg"
        >
          <WaitlistForm compact />
          <div className="mt-4 flex items-center gap-5">
            <a
              data-testid="hero-demo-trigger"
              href="#producto"
              className="group inline-flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-emerald-400"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 group-hover:border-emerald-500/50">
                <Play className="h-3 w-3 fill-current" />
              </span>
              Ver demostración en vivo
            </a>
            <span className="mono hidden text-[11px] uppercase tracking-widest text-slate-600 sm:block">
              Sin tarjeta · 2 min
            </span>
          </div>
        </motion.div>
      </div>

      <PhoneCard />
    </div>
  </section>
);
